
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useAuthors } from '@/hooks/useAuthors';
import { useBooks } from '@/hooks/useBooks';
import { DialogFooter } from '@/components/ui/dialog';
import FormHeader from './CreateReportForm/FormHeader';
import BookSelection from './CreateReportForm/BookSelection';
import StatusSelection from './CreateReportForm/StatusSelection';
import { useBookFormLogic } from './CreateReportForm/useBookFormLogic';
import { editRoyaltyReportSchema, FormData } from './EditReportForm/formSchema';
import { useFormInitialization } from './EditReportForm/useFormInitialization';
import { useFormSubmit } from './EditReportForm/useFormSubmit';

interface GroupedReport {
  authorId: string;
  year: number;
  quarter: number;
  reports: any[];
  totalSales: number;
  totalAmount: number;
  status: string;
  countries: Set<string>;
}

interface EditReportFormProps {
  reportGroup: GroupedReport;
  onSuccess?: () => void;
}

export default function EditReportForm({ reportGroup, onSuccess }: EditReportFormProps) {
  const { data: authors, isLoading: isLoadingAuthors } = useAuthors();
  const { data: books, isLoading: isLoadingBooks } = useBooks();
  
  const form = useForm<FormData>({
    resolver: zodResolver(editRoyaltyReportSchema),
    defaultValues: {
      authorId: reportGroup.authorId,
      year: reportGroup.year,
      quarter: reportGroup.quarter,
      status: reportGroup.status as 'draft' | 'published',
      books: [],
    },
  });

  const {
    bookFormatGroups,
    toggleBook,
    addBookFormat,
    removeBookFormat,
    updateBookData
  } = useBookFormLogic(form);

  useFormInitialization(form, reportGroup);
  const { onSubmit, updateReport } = useFormSubmit(onSuccess);

  const selectedAuthorId = form.watch('authorId');
  const authorBooks = books?.filter(book => book.authorId === selectedAuthorId) || [];

  return (
    <div className="w-full max-w-none">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormHeader
            form={form}
            authors={authors as any[]}
            isLoadingAuthors={isLoadingAuthors}
          />
          
          {selectedAuthorId && authorBooks.length > 0 && (
            <BookSelection
              form={form}
              authorBooks={authorBooks}
              bookFormatGroups={bookFormatGroups}
              onToggleBook={toggleBook}
              onUpdateBookData={updateBookData}
              onAddFormat={addBookFormat}
              onRemoveFormat={removeBookFormat}
            />
          )}
          
          <StatusSelection form={form} />
          
          <DialogFooter className="pt-4 sticky bottom-0 bg-white border-t mt-6">
            <Button 
              type="submit" 
              disabled={updateReport.isPending}
              className="bg-royalty-600 hover:bg-royalty-700 w-full sm:w-auto"
            >
              {updateReport.isPending ? 'Updating...' : 'Update Reports'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
